import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { COPY } from "../shared/copy";
import { api } from "../lib/api";
import { supabase } from "../lib/supabase";
import { pushConfigured, resetPushSetup, syncCurrentDevice } from "../lib/push";
import { PushSetup } from "../components/PushSetup";
import { remainingWindow } from "../components/Timer";
import { Avatar } from "../components/Avatar";
import { rankBadge, seasonHighlights, shortenReveal } from "../shared/format";
import { highlightName } from "../shared/highlight";
import { cleanProse } from "../shared/sanitize";
import { NewLeague } from "./NewLeague";
import {
  getMyLeagues, pickLeague, rememberLeague, getLeagueState, getLiveDrops, getLeaderboardWithMovement,
  getRecentReveals, myApprovedCount, daysRemaining, weekNumber, type Member, type MyLeague,
} from "../lib/league";

// League home.
//
// The screen answers one question at a time, in the order that matters right
// now, instead of stacking every card it owns:
//   1. can they even receive a drop yet
//   2. is there something to play
//   3. where do they stand
//   4. do they owe the league an update
//   5. what has everyone been up to
// A finished season replaces all of that with the final standings, because at
// that point nothing else on the page is actionable.
export function Home() {
  const { leagueId: routeLeague } = useParams();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<any>(null);
  const [leagueId, setLeagueId] = useState<string | null>(routeLeague ?? null);
  const [myLeagues, setMyLeagues] = useState<MyLeague[]>([]);
  const [nameInput, setNameInput] = useState("");
  const [savingName, setSavingName] = useState(false);

  async function saveName() {
    if (!user || !nameInput.trim()) return;
    setSavingName(true);
    await supabase.from("users").update({ display_name: nameInput.trim() }).eq("id", user.id);
    window.location.reload();
  }

  useEffect(() => {
    (async () => {
      if (!user) return;
      // Quietly make sure the server is pointed at this device. A push id that
      // rotated while the app was closed would otherwise leave someone silently
      // unreachable while the app still told them notifications were on.
      syncCurrentDevice();
      const leagues = await getMyLeagues(user.id);
      setMyLeagues(leagues);

      // Claim any invite waiting on this email, every time, not only when they
      // belong to nothing. Someone already in one league who gets invited to a
      // second is the normal case, and gating this on "has no league" meant
      // signing in with the invited address quietly did nothing.
      let claimed: string | null = null;
      try {
        const r = await api<{ leagueId: string | null }>(`/api/join`, { body: { op: "auto" } });
        claimed = r.leagueId;
      } catch { /* not fatal, they may already be joined */ }
      if (claimed && !leagues.some((l) => l.id === claimed)) {
        rememberLeague(claimed);
        navigate(`/league/${claimed}/onboarding`, { replace: true });
        return;
      }

      let lid = routeLeague ?? null;
      if (!lid) lid = pickLeague(leagues);
      if (!lid) {
        const pending = localStorage.getItem("pending_invite");
        if (pending) { navigate(`/join/${pending}`, { replace: true }); return; }
        setState({ empty: true });
        return;
      }
      setLeagueId(lid);
      rememberLeague(lid);

      const s = await getLeagueState(lid, user.id);
      if (!s.season) { setState({ ...s, noSeason: true }); return; }
      const [liveDrops, board, recent, approved] = await Promise.all([
        getLiveDrops(s.season.id),
        getLeaderboardWithMovement(s.season.id, s.members as Member[]),
        getRecentReveals(s.season.id),
        myApprovedCount(s.season.id, user.id),
      ]);
      // Ask each live drop how many questions it holds and whether this player is
      // already through it, so home invites them only into drops they can play.
      const live = await Promise.all(
        (liveDrops ?? []).map(async (d: any) => {
          try {
            const l = await api<{ questionCount: number; youAreDone: boolean }>(
              `/api/drops/landing`, { query: { dropId: d.id } }
            );
            return { ...d, questionCount: l.questionCount, youAreDone: l.youAreDone };
          } catch {
            return { ...d, questionCount: 0, youAreDone: true };
          }
        })
      );
      setState({ ...s, live, board, recent, approved });
    })();
  }, [routeLeague, user]);

  if (!state) return <div className="app"><div className="loading">Loading…</div></div>;
  if (state.empty) return <NewLeague firstTime />;

  const { league, season, me, live, board, recent, approved } = state;
  const seasonOver = season?.status === "complete";
  // Anything short of finished: a draft season still needs everyone's updates,
  // since inventory is what makes the first drop possible.
  const canCheckIn = season?.status !== "complete";
  const week = season ? weekNumber(season.starts_on) : 1;
  const left = season ? daysRemaining(season.ends_on) : 0;
  const myRank = board?.find((b: any) => b.user_id === user!.id);
  const top3 = (board ?? []).slice(0, 3);
  const toPlay = (live ?? []).filter((d: any) => !d.youAreDone);
  const awaiting = (live ?? []).filter((d: any) => d.youAreDone);

  const needsName = !!me && !(me.display_name && me.display_name.trim());
  const needsPush = !!me && pushConfigured() && me.push_status !== "push_ready";
  const setupDone = [!needsName, !needsPush].filter(Boolean).length;
  const setupLeft = (needsName || needsPush) && !seasonOver;

  // Only crown somebody when points were actually scored, so an abandoned season
  // does not hand a trophy to whoever happens to sort first at zero.
  const champion = seasonOver && board?.length && board[0].points > 0 ? board[0] : null;
  const highlights = seasonOver ? seasonHighlights(board ?? []) : [];

  return (
    <div className="app">
      <div className="header">
        <h1>{league?.name ?? "League"}</h1>
        {me?.role === "commissioner" && (
          <button className="link" onClick={() => navigate(`/league/${leagueId}/commissioner`)}>Commissioner</button>
        )}
      </div>

      {/* One address is expected to belong to several leagues. Without this,
          whichever one the database returned first was the only reachable one. */}
      {myLeagues.length > 1 && (
        <select
          className="input"
          style={{ fontWeight: 700 }}
          value={leagueId ?? ""}
          onChange={(e) => { rememberLeague(e.target.value); window.location.href = `/league/${e.target.value}`; }}
        >
          {myLeagues.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      )}

      <div className="row">
        {seasonOver
          ? <span className="pill">Season complete</span>
          : <><span className="pill">Week {week}</span><span className="pill">{left} days left</span></>}
      </div>

      {seasonOver ? (
        <>
          {/* The season is over, so the finale is the point of this screen. */}
          <div className="card live">
            <span className="tag">The season is done</span>
            <p>Take the walk through how it went, who won, and everything y&apos;all shared.</p>
            <button className="btn primary" onClick={() => navigate(`/league/${leagueId}/finale`)}>
              See how the season ended
            </button>
          </div>

          {/* A season should end on somebody's name, not a frozen list. */}
          {champion && (
            <div className="card champion">
              <span className="trophy">🏆</span>
              <p className="crown-label">Season champion</p>
              <Avatar id={champion.user_id} name={champion.name} size={72} />
              <h2 className="champion-name">{champion.name}</h2>
              <p className="muted">
                {champion.points} point{champion.points === 1 ? "" : "s"}
                {champion.user_id === user!.id ? " · that's you" : ""}
              </p>
            </div>
          )}

          {highlights.length > 0 && (
            <div className="card">
              <span className="tag">Season highlights</span>
              {highlights.map((h) => (
                <div className="highlight" key={h.label}>
                  <span className="highlight-label">{h.label}</span>
                  <span className="highlight-name">{h.name}</span>
                  <span className="muted highlight-detail">{h.detail}</span>
                </div>
              ))}
            </div>
          )}

          <div className="card">
            <span className="tag">Final standings</span>
            {(board ?? []).map((e: any) => (
              <StandingRow key={e.user_id} entry={e} isMe={e.user_id === user!.id} />
            ))}
            <p className="muted">{COPY.leaderboard.scoring}</p>
          </div>

          {recent && recent.length > 0 && <RecentlyCard recent={recent} members={state.members} />}
        </>
      ) : (
        <>
          {setupLeft && (
            <>
              <div className="card">
                <span className="tag">Finish setup: {setupDone} of 2 done</span>
                <ul className="checklist">
                  <li className={needsName ? "todo" : "done"}>
                    <span className="mark">{needsName ? "○" : "✓"}</span>
                    <span>Add your name</span>
                  </li>
                  <li className={needsPush ? "todo" : "done"}>
                    <span className="mark">{needsPush ? "○" : "✓"}</span>
                    <span>Get your phone to ping you</span>
                  </li>
                </ul>
                <p className="muted">Questions drop at random times. Finish these and you won&apos;t miss any.</p>
              </div>
              {needsName && (
                <div className="card live">
                  <span className="tag">Your name</span>
                  <p>Add your name so your boys know who&apos;s who.</p>
                  <input className="input" placeholder="Your name" value={nameInput} onChange={(e) => setNameInput(e.target.value)} />
                  <button className="btn primary" disabled={!nameInput.trim() || savingName} onClick={saveName}>Save name</button>
                </div>
              )}
              {needsPush && <PushSetup leagueId={leagueId!} onDone={() => window.location.reload()} />}
            </>
          )}

          {toPlay.length > 0 ? (
            <div className="card live">
              <span className="tag">Drop {String(toPlay[0].sequence_number ?? "").padStart(2, "0")}</span>
              <h2>{COPY.drop.dropTitle(toPlay[0].questionCount || 2)}</h2>
              <p className="muted">{remainingWindow(toPlay[0].closes_at)} left to play</p>
              <button className="btn primary" onClick={() => navigate(`/league/${leagueId}/drop/${toPlay[0].id}`)}>
                {COPY.home.dropLiveCta}
              </button>
              {toPlay.length > 1 && <p className="muted">{toPlay.length - 1} more waiting after this one.</p>}
            </div>
          ) : awaiting.length > 0 ? (
            <div className="card">
              <h2>{COPY.waiting.lockedIn}</h2>
              <p className="muted">{COPY.waiting.resultsWhen}</p>
              <button className="link" onClick={() => navigate(`/league/${leagueId}/drop/${awaiting[0].id}`)}>
                Check if results are in
              </button>
            </div>
          ) : (
            <div className="card">
              <h2>{COPY.home.nothingLive}</h2>
              <p className="muted">{COPY.home.stayReady}</p>
            </div>
          )}

          <div className="card">
            <div className="header">
              <span className="tag">Leaderboard</span>
              <button className="link" onClick={() => navigate(`/league/${leagueId}/leaderboard`)}>
                {COPY.home.viewLeaderboard}
              </button>
            </div>
            {top3.map((e: any) => (
              <StandingRow key={e.user_id} entry={e} isMe={e.user_id === user!.id} />
            ))}
            {myRank && myRank.rank > 3 && (
              <StandingRow entry={myRank} isMe />
            )}
          </div>

          {/* What everyone has been up to sits directly under the standings:
              competition at the top, life underneath. Owing an update is a
              smaller, more routine thing, so it goes last. */}
          {recent && recent.length > 0 && <RecentlyCard recent={recent} members={state.members} />}

          {/* Only while an update can still be used by a future drop. */}
          {canCheckIn && (
            <div className="card">
              {approved > 0 ? (
                <>
                  <span className="tag">Your questions</span>
                  <p>{COPY.home.questionsReady(approved)}</p>
                  <p className="muted">{COPY.home.questionsReadySub}</p>
                  <button className="link" onClick={() => navigate(`/league/${leagueId}/checkin`)}>Add another update</button>
                </>
              ) : (
                <>
                  <p>{COPY.home.checkInDue}</p>
                  <button className="btn accent" onClick={() => navigate(`/league/${leagueId}/checkin`)}>
                    {COPY.home.checkInCta}
                  </button>
                </>
              )}
            </div>
          )}
        </>
      )}

      <p className="muted center" style={{ fontSize: 13 }}>
        Signed in as {user?.email}
        {" · "}
        <button className="link" onClick={async () => { await signOut(); window.location.href = "/"; }}>Sign out</button>
        {!needsPush && (
          <>
            {" · "}
            <button
              className="link"
              onClick={async () => {
                if (!window.confirm("Set up notifications again on THIS phone?\n\nYour other devices are not affected.")) return;
                await resetPushSetup(leagueId!);
                window.location.reload();
              }}
            >
              Redo notifications on this phone
            </button>
          </>
        )}
      </p>
      {/* Host-only, so Brian can check the finale before the league sees it.
          The screen itself re-checks the role, so this link is convenience, not
          the security boundary. */}
      {me?.role === "commissioner" && !seasonOver && (
        <p className="muted center" style={{ fontSize: 12 }}>
          <button className="link" onClick={() => navigate(`/league/${leagueId}/finale`)}>
            Preview the finale
          </button>
        </p>
      )}

      <p className="muted center" style={{ fontSize: 12 }}>
        <button className="link" onClick={() => navigate("/new-league")}>Start a league of your own</button>
        {" · "}
        <button className="link" onClick={() => navigate("/diagnostics")}>Notifications not working?</button>
      </p>
      <p className="muted center" style={{ fontSize: 11, opacity: 0.5 }}>Build {__BUILD_ID__}</p>
    </div>
  );
}

// One standings row. Home's preview and the final standings both use it so the
// two can never drift apart.
function StandingRow({ entry, isMe }: { entry: any; isMe: boolean }) {
  return (
    <div className={`lb-row ${isMe ? "me" : ""}`}>
      <span className="lb-rank">{rankBadge(entry.rank, entry.points)}</span>
      <Avatar id={entry.user_id} name={entry.name} size={30} />
      <span className="lb-name">
        {entry.name}{isMe ? " (you)" : ""}
        {entry.attempted_count > 0 && (
          <>
            <br />
            <span className="muted lb-record">{entry.correct_count}/{entry.attempted_count} correct</span>
          </>
        )}
      </span>
      <Movement value={entry.movement} />
      <span className="lb-points">{entry.points}</span>
    </div>
  );
}

// Rank change since the previous revealed drop. Nothing is drawn before there is
// a prior drop to compare against, and a player who held their spot shows no
// arrow rather than a meaningless zero.
export function Movement({ value }: { value: number | null | undefined }) {
  if (value == null || value === 0) return null;
  return (
    <span className={`move ${value > 0 ? "up" : "down"}`}>
      {value > 0 ? `▲${value}` : `▼${Math.abs(value)}`}
    </span>
  );
}

const RECENTLY_STEP = 3;

function RecentlyCard({ recent, members }: { recent: any[]; members?: Member[] }) {
  const [shown, setShown] = useState(RECENTLY_STEP);
  const nameOf = new Map((members ?? []).map((m) => [m.user_id, m.display_name]));
  const visible = recent.slice(0, shown);
  const remaining = recent.length - visible.length;

  return (
    <div className="card">
      <span className="tag">{COPY.home.recentlyHeading}</span>
      <ul className="recently">
        {visible.map((r: any) => {
          // cleanProse runs here as well as at generation, so updates written
          // before the em dash rule existed read correctly too, with no need to
          // rewrite anybody's stored history.
          const parts = highlightName(shortenReveal(cleanProse(r.reveal_context ?? "")), nameOf.get(r.subject_user_id));
          return (
            <li key={r.drop_question_id}>
              {parts.map((part, i) =>
                part.isName
                  ? <b key={i} className="who-name-hl">{part.text}</b>
                  : <span key={i}>{part.text}</span>
              )}
            </li>
          );
        })}
      </ul>
      {remaining > 0 && (
        <button className="link" onClick={() => setShown((n) => n + RECENTLY_STEP)}>
          Show {Math.min(remaining, RECENTLY_STEP)} more
          {remaining > RECENTLY_STEP ? ` (${remaining} left)` : ""}
        </button>
      )}
    </div>
  );
}
